// This store's online orders for one filter and search, and the store's steps
// on them. Online only: orders come from Omni Connect and are shared by every
// till in the store. Each step is saved on the server first, then the channel
// is told with upsertOfdOrderStatus; when only the channel update fails the
// step stands, the update is kept as pending and the panel offers to send it
// again. A store bill shipped home (storeSale) has no channel: its steps are
// saved on the server only.
import { useCallback, useEffect, useRef, useState } from "react";
import { fashionPosService, generateAggregatorBill, upsertOfdOrderStatus } from "../../../../services";
import { acknowledgeOrderNotification } from "../../../../services/orderServices";
import {
  buildAcceptOnlineOrderMutation,
  buildCancelOnlineOrderMutation,
  buildHandOverOnlineOrderMutation,
  buildOnlineOrdersQuery,
  buildReadyOnlineOrderMutation,
  buildShipOnlineOrderMutation,
  CHANNEL_STATUS,
  readChannelPending,
  readOnlineOrders,
  writeChannelPending,
} from "../../../../utils/fashion/onlineOrders";
import { readMessage } from "../../../../utils/fashion/holds";

export const SEARCH_DELAY_MS = 300;
export const BILL_RETRY_MS = 5000;
const CHANNEL_WARN = "The online store wasn't told yet. Use “Update channel” to send it again.";

// Tell the channel; true when it took the update.
async function tellChannel(orderId, code, reasonCode = null) {
  try {
    const res = await upsertOfdOrderStatus(orderId, code, null, reasonCode, "N");
    return res?.data?.data?.upsertOfdOrderStatus?.status === "200";
  } catch {
    return false;
  }
}

export default function useFashionOnlineOrders(tillData, isOnline, filter, search) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const tillRef = tillData?.tillAccess?.cwrTill?.till;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [state, setState] = useState({ rows: [], status: "idle" });
  const latest = useRef(0);
  const retryTimer = useRef(null);
  const [pending, setPending] = useState(() => readChannelPending(bunitId));

  useEffect(() => setPending(readChannelPending(bunitId)), [bunitId]);

  // Record or clear an order's channel update that didn't get through.
  const markPending = useCallback(
    (orderId, entry) => {
      const next = { ...readChannelPending(bunitId) };
      if (entry) next[orderId] = { ...entry, at: Date.now() };
      else delete next[orderId];
      writeChannelPending(bunitId, next);
      setPending(next);
    },
    [bunitId]
  );

  const reload = useCallback(async () => {
    if (!bunitId || !online) return;
    const call = ++latest.current;
    setState((s) => ({ ...s, status: "loading" }));
    const rows = readOnlineOrders(await fashionPosService(buildOnlineOrdersQuery(bunitId, filter, search)));
    if (call !== latest.current) return; // a newer filter or search went out
    setState((s) => (rows === null ? { rows: s.rows, status: "error" } : { rows, status: "done" }));
  }, [bunitId, online, filter, search]);

  useEffect(() => {
    if (!bunitId || !online) return undefined;
    const timer = setTimeout(reload, search ? SEARCH_DELAY_MS : 0);
    return () => clearTimeout(timer);
  }, [bunitId, online, reload, search]);

  useEffect(() => () => clearTimeout(retryTimer.current), []);

  // Server step, then the channel. `to` is the store status the step reaches.
  // `reasonCode` goes with a cancel; `channelDone` skips the push when the
  // channel already has this status (the customer cancelled online).
  const step = useCallback(
    async (orderId, to, mutation, field, fallback, { reasonCode = null, channelDone = false } = {}) => {
      const res = readMessage(await fashionPosService(mutation), field, fallback);
      if (res.ok) {
        const code = CHANNEL_STATUS[to];
        const told = channelDone || (await tellChannel(orderId, code, reasonCode));
        if (told) markPending(orderId, null);
        else {
          markPending(orderId, { code, reason: reasonCode });
          res.warn = CHANNEL_WARN;
        }
        // Deciding the order here retires its pending popup, as the Aggregator screen does (BUG-2373).
        if (to === "ACC" && tillRef) Promise.resolve(acknowledgeOrderNotification(tillRef, orderId)).catch(() => {});
      }
      reload();
      return res;
    },
    [reload, tillRef, markPending]
  );

  const accept = useCallback(
    (orderId, mode) => step(orderId, "ACC", buildAcceptOnlineOrderMutation(orderId, bunitId, mode), "acceptFashionOnlineOrder", "Couldn't accept the order. Try again."),
    [step, bunitId]
  );
  const ready = useCallback(
    (orderId, storeSale = false) =>
      step(orderId, "RDY", buildReadyOnlineOrderMutation(orderId), "readyFashionOnlineOrder", "Couldn't mark the order packed. Try again.", { channelDone: storeSale }),
    [step]
  );
  const handOver = useCallback(
    (orderId, code) => step(orderId, "DLV", buildHandOverOnlineOrderMutation(orderId, code), "handOverFashionOnlineOrder", "Couldn't hand the order over. Try again."),
    [step]
  );
  const ship = useCallback(
    (orderId, courier, awbNo, storeSale = false) =>
      step(orderId, "SHP", buildShipOnlineOrderMutation(orderId, courier, awbNo), "shipFashionOnlineOrder", "Couldn't mark the order shipped. Try again.", { channelDone: storeSale }),
    [step]
  );
  const cancel = useCallback(
    (orderId, reason, reasonCode, channelDone = false) =>
      step(orderId, "CAN", buildCancelOnlineOrderMutation(orderId, reason), "cancelFashionOnlineOrder", "Couldn't cancel the order. Try again.", {
        reasonCode: reasonCode || "unspecified",
        channelDone,
      }),
    [step]
  );

  // Send the channel status for the store's current step again.
  const resendChannel = useCallback(
    async (orderId, code) => {
      const reason = readChannelPending(bunitId)[orderId]?.reason || null;
      const ok = await tellChannel(orderId, code, reason);
      if (ok) markPending(orderId, null);
      reload();
      return ok ? { ok: true, message: "Online store updated." } : { ok: false, message: "The online store still didn't take the update. Try again shortly." };
    },
    [reload, bunitId, markPending]
  );

  // The aggregator bill: the same server call the Aggregator screen makes.
  // "202" means the server is still making it; the list is checked again shortly.
  const bill = useCallback(
    async (orderId) => {
      let result;
      try {
        result = (await generateAggregatorBill(orderId))?.data?.data?.generateAggregatorBill;
      } catch {
        result = null;
      }
      if (result?.status === "200") {
        reload();
        return { ok: true, message: result.message || `Billed${result.documentNo ? `: ${result.documentNo}` : ""}.`, documentNo: result.documentNo || "" };
      }
      if (result?.status === "202") {
        clearTimeout(retryTimer.current);
        retryTimer.current = setTimeout(reload, BILL_RETRY_MS);
        return { ok: true, pending: true, message: result.message || "The bill is being made. The list will refresh shortly." };
      }
      return { ok: false, message: result?.message || "Couldn't bill the order. Try again." };
    },
    [reload]
  );

  return { ...state, pending, online, reload, accept, ready, handOver, ship, cancel, bill, resendChannel };
}
